import React, { useEffect, useState } from 'react';
import {
  experimentEnabled,
  finishExperiment,
  getPurchaseData,
  PurchaseData,
} from 'coaching/experiment/SignInFunnelExperiment';
import CurrentUser from 'auth/CurrentUser';
import { purchaseCoaching } from 'coaching/actions/CoachingActions';
import { SavedCoachingReservation } from 'coaching/containers/GetAvailableReservationsContainer';
import CoachingSelfServeSuccessView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeSuccessView';
import CoachingIsolatedViewHeader from 'coaching/components/CoachingIsolatedViewHeader';
import Container from 'components/Container';
import CoachingSelfServeFailureView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeFailureView';
import CoachingSelfServePostPurchaseView from 'coaching/components/CoachingSelfServeView/CoachingSelfServePostPurchaseView';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import { pushCustomEvent } from 'lib/googleTagManager';
import { withRouter } from 'app/context/Router';
import { InjectedRouter } from 'react-router';
import GTMScript from 'components/GTMScript';
import Cookies from 'lib/Cookies';
import CoachingConstants from 'coaching/CoachingConstants';

export type Props = {
  router: InjectedRouter;
};

function FinalizePurchase(props: Props) {
  type State = {
    submittingPayment: boolean;
    showError: boolean;
    purchaseFailureReason?: 'PURCHASE_FAILURE_DUPLICATE_PURCHASE';
    purchaseSucceeded: boolean;
    participantUUID: string | null;
    experimentEnabled?: boolean;
    purchaseData?: PurchaseData;
  };
  const [state, setState] = useState<State>({
    submittingPayment: true,
    showError: false,
    purchaseSucceeded: false,
    participantUUID: null,
  });
  useEffect(() => {
    Cookies.expire(CoachingConstants.IS_INTENDED_COACHING_USER_COOKIE);
    const enabled = experimentEnabled();
    const purchaseData = getPurchaseData();
    setState({ ...state, experimentEnabled: enabled, purchaseData });
    finishExperiment();
    completePurchase(purchaseData!);
  }, []);

  const completePurchase = (purchaseData: PurchaseData) => {
    setState(previousState => {
      return { ...previousState, submittingPayment: true };
    });
    const selectedStudent = getSelectedStudentInfo(purchaseData);
    const {
      selectedReservation,
      selectedSubject,
      selectedGradeLevel,
      source,
      userTimezone,
    } = purchaseData;

    const user = CurrentUser.getUser();
    const errorString = 'FinalizePurchase - purchase failed';
    return purchaseCoaching(
      selectedReservation.startDateTime.toISOString(),
      selectedReservation.endDateTime.toISOString(),
      selectedReservation.coachUuid,
      source,
      user.getUuid(),
      userTimezone,
      [selectedSubject],
      selectedStudent,
      selectedGradeLevel,
      (selectedReservation as SavedCoachingReservation).reservationUuid
    )
      .then(res => {
        setState(previousState => {
          return { ...previousState, submittingPayment: false };
        });

        if (res.error?.code === 'PURCHASE_FAILURE_CARD_FAILURE') {
          // Console errors will go to rollbar
          console.error('Failed to signup for coaching because of CC issue in funnel experiment');
          
          setState(previousState => {
            return { ...previousState, showError: true };
          });
          return;
        }

        if (res.error?.code === 'PURCHASE_FAILURE_DUPLICATE_PURCHASE') {
          setState(previousState => {
            return {
              ...previousState,
              showError: true,
              purchaseFailureReason: 'PURCHASE_FAILURE_DUPLICATE_PURCHASE',
            };
          });
          return;
        }

        // Any other unknown error goes to the catch-all error screen
        if (res.error || res.participantUuid === null) {
          console.error(errorString);

          setState(previousState => {
            return { ...previousState, showError: true };
          });
          return;
        }

        // Track success in GTM via custom trigger
        pushCustomEvent('free_session_signup_completed');

        setState(previousState => {
          return {
            ...previousState,
            participantUUID: res.participantUuid,
            purchaseSucceeded: true,
          };
        });
      })
      .catch(error => {
        console.error(`${errorString}, ${error}`);
        setState(previousState => {
          return { ...previousState, submittingPayment: false, showError: true };
        });
      });
  };

  function getSelectedStudentInfo(purchaseData: PurchaseData): StudentInfo {
    if (CurrentUser.getUser()?.isStudent()) {
      return {
        name: CurrentUser.getUser().getName(),
        uuid: CurrentUser.getUser().getUuid(),
      };
    } else {
      return {
        name: purchaseData.student.studentName,
      };
    }
  }

  return (
    <div className="CoachingIsolatedView">
      <GTMScript />
      <CoachingIsolatedViewHeader />
      <Container size="medium">
        {state.submittingPayment && <CenteredLoadingIndicator />}
        {state.showError && (
          <CoachingSelfServeFailureView failureReason={state.purchaseFailureReason} />
        )}
        {state.purchaseSucceeded &&
          renderSuccess(
            state.participantUUID!,
            state.purchaseData!,
            getSelectedStudentInfo(state.purchaseData!)
          )}
      </Container>
    </div>
  );
}

const renderSuccess = (
  participantUuid: string,
  purchaseData: PurchaseData,
  studentInfo: StudentInfo
) => {
  const { selectedReservation, userTimezone } = purchaseData;
  const user = CurrentUser.getUser();
  const hasPostPurchaseFlowFlag = user.hasFeature('coaching_connect_student_account.web.nov-20');
  const purchasingForUnconnectedStudent = !CurrentUser.getUser()?.isStudent();
  const Component =
    hasPostPurchaseFlowFlag && purchasingForUnconnectedStudent
      ? CoachingSelfServePostPurchaseView
      : CoachingSelfServeSuccessView;
  return (
    <Component
      childName={studentInfo?.name}
      reservation={selectedReservation}
      userTimezone={userTimezone}
      userEmail={CurrentUser.getUser().getEmail()}
      childEmail={undefined}
      participantUUID={participantUuid}
      selectedSubject={purchaseData.selectedSubject}
    />
  );
};

type StudentInfo = {
  uuid?: string;
  name: string;
};

export default withRouter(FinalizePurchase);
